import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar
} from '@/components/ui/sidebar';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { CURRENT_SAMSON_VERSION, CURRENT_SAMSON_VERSION_TIMESTAMP } from '@app/version';
import { format } from 'date-fns';
import { ChevronRight } from 'lucide-react';
import { useLocation, useNavigate } from "react-router-dom";
import { useShallow } from 'zustand/react/shallow';
import Logo from '~/components/layout/Logo';
import { navigationElementsAdmin, navigationElementsUser } from '~/navigation';
import { useSidebarStore } from '~/stores/sidebar-store';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

const AppSidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const sidebar = useSidebar();

    const { role } = useUserStore(
        useShallow((state) => ({
            username: `${state.user?.firstName} ${state.user?.lastName}`,
            role: state.user?.role
        }))
    );

    const { isGroupOpen, setGroupOpen } = useSidebarStore();

    const navigationElements = role === UserRole.ADMIN || role === UserRole.OPERATOR
        ? navigationElementsAdmin
        : navigationElementsUser;

    return (
        <Sidebar collapsible="icon" className="sticky">
            <SidebarHeader className='bg-white'>
                <Logo collapsed={!sidebar.open} type="light" />
            </SidebarHeader>

            <SidebarContent className='w-full bg-white'>
                {navigationElements.map((group, groupIdx) => {
                    // Check if any item in this group is currently active
                    const hasActiveItem = group.items.some(
                        (item) => location.pathname === item.url
                    );

                    const menuContent = (
                        <SidebarMenu>
                            {group.items.map((item, itemIdx) => (
                                <SidebarMenuItem key={`sidebar-item-${itemIdx}-${item.title}`}>
                                    <SidebarMenuButton asChild isActive={location.pathname === item.url}>
                                        <a href={item.url} onClick={(e) => {
                                            e.preventDefault();
                                            navigate(item.url);
                                        }}>
                                            {item.icon}
                                            <span>{item.title}</span>
                                        </a>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            ))}
                        </SidebarMenu>
                    );

                    if (group.collapsible) {
                        const fallbackOpen = hasActiveItem || !!group.defaultOpen;
                        const isOpen = isGroupOpen(group.title, fallbackOpen);

                        return (
                            <Collapsible
                                key={`sidebar-group-${groupIdx}-${group.title}`}
                                open={isOpen}
                                onOpenChange={(open) => setGroupOpen(group.title, open)}
                                className="group/collapsible"
                            >
                                <SidebarGroup>
                                    {sidebar.open && (
                                        <SidebarGroupLabel asChild>
                                            <CollapsibleTrigger className="flex w-full items-center">
                                                {group.title}
                                                <ChevronRight className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-90" />
                                            </CollapsibleTrigger>
                                        </SidebarGroupLabel>
                                    )}
                                    <CollapsibleContent>
                                        <SidebarGroupContent>
                                            {menuContent}
                                        </SidebarGroupContent>
                                    </CollapsibleContent>
                                </SidebarGroup>
                            </Collapsible>
                        );
                    }

                    return (
                        <SidebarGroup key={`sidebar-group-${groupIdx}-${group.title}`}>
                            {sidebar.open && (
                                <SidebarGroupLabel>{group.title}</SidebarGroupLabel>
                            )}
                            <SidebarGroupContent>
                                {menuContent}
                            </SidebarGroupContent>
                        </SidebarGroup>
                    );
                })}
            </SidebarContent>

            <SidebarFooter className='bg-white'>
                {sidebar.open ? (
                    <div className='flex flex-col w-full items-center pb-4 gap-1'>
                        <span className='text-center'>
                            Samson v{CURRENT_SAMSON_VERSION}
                        </span>
                        <span className='text-center'>
                            ({format(CURRENT_SAMSON_VERSION_TIMESTAMP, "yyyy-MM-dd HH:mm")})
                        </span>
                    </div>
                ) : null}
            </SidebarFooter>
        </Sidebar>
    );
};

export default AppSidebar;